import React, { useState, useMemo, useRef, useEffect } from 'react';
import * as LucideIcons from 'lucide-react';
import { Copy, Check, Search, Shapes, RefreshCw, GripVertical } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';

const IconBrowserPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedIcon, setCopiedIcon] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const { notify } = useNotifications();

  useEffect(() => {
    const handleClickOutside = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get all icon names (filter out non-icon exports)
  const allIconNames = useMemo(() => {
    return Object.keys(LucideIcons).filter(name => {
      return /^[A-Z][a-zA-Z0-9]*$/.test(name) &&
             !name.endsWith('Icon') &&
             name !== 'default' &&
             name !== 'createLucideIcon';
    }).sort();
  }, []);

  // Fuzzy filter based on search term
  const filteredIcons = useMemo(() => {
    if (!searchTerm.trim()) return allIconNames;
    const term = searchTerm.toLowerCase();
    return allIconNames.filter(name =>
      name.toLowerCase().includes(term)
    );
  }, [allIconNames, searchTerm]);

  // Copy icon name to clipboard
  const handleCopyIcon = async (iconName) => {
    try {
      await navigator.clipboard.writeText(iconName);
      setCopiedIcon(iconName);
      notify.success(`Copied ${iconName}`);
      setTimeout(() => setCopiedIcon(null), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = iconName;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedIcon(iconName);
      notify.success(`Copied ${iconName}`);
      setTimeout(() => setCopiedIcon(null), 2000);
    }
  };

  return (
    <div className="dev-module icon-browser-page">
      <div className="dev-module-header">
        <div className="dev-module-header-left">
          <Shapes size={20} style={{ color: 'var(--color-primary)' }} />
          <h1>Icon Browser</h1>
          <span className="dev-module-meta">{filteredIcons.length} of {allIconNames.length} icons</span>
        </div>
        <div className="dev-module-header-right">
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Search size={14} style={{ position: 'absolute', left: '8px', color: 'var(--color-gray-400)', pointerEvents: 'none' }} />
            <input className="form-input" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search icons..." style={{ paddingLeft: '28px', width: '200px', height: '32px' }} />
          </div>
          <div className="action-menu" ref={menuRef}>
            <button className="action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions"><GripVertical size={16} /></button>
            {menuOpen && (
              <div className="action-menu-dropdown">
                <button className="action-menu-item" onClick={() => { setMenuOpen(false); setSearchTerm(''); }}><RefreshCw size={16} /><span>Reset</span></button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="dev-module-content">
        <div className="icon-grid">
          {filteredIcons.map(iconName => {
            const IconComponent = LucideIcons[iconName];
            const isCopied = copiedIcon === iconName;
            return (
              <button
                key={iconName}
                className={`icon-card ${isCopied ? 'copied' : ''}`}
                onClick={() => handleCopyIcon(iconName)}
                title={`Click to copy: ${iconName}`}
              >
                <div className="icon-preview">
                  <IconComponent size={24} />
                </div>
                <div className="icon-name">{iconName}</div>
                <div className="icon-copy-indicator">
                  {isCopied ? <Check size={12} /> : <Copy size={12} />}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default IconBrowserPage;
